import { useQueryParams } from '@marqvision/shared';
import { useSelectedBrandId } from '~/hooks';
import { DomainNamesRouteEnum } from '~/router/@types';
import {
  MarqBrandEnumsWebsiteBrandSortedColumnEnum,
  MarqCommonEnumsSortedOrderEnum,
  MarqCommonEnumsSwWebsiteBrandDomainNamesTabEnum,
  MarqCommonModelLocalDateRange,
  MarqCommonEnumsCommentCommentUserTypeEnum,
  MarqFoundationEnumsCountryCodeEnum,
} from '~/swagger/data-contracts';
import { parseValueToValueArray } from '~/utils';
import { SORT_LIST } from '../../@constants';
import { useTab } from '../../@hooks/useTab';
import { FilterQueryParams, SortValue } from '../../@types';

const requestTab = {
  [DomainNamesRouteEnum.Scanned]: MarqCommonEnumsSwWebsiteBrandDomainNamesTabEnum.Scanned,
  [DomainNamesRouteEnum.Flagged]: MarqCommonEnumsSwWebsiteBrandDomainNamesTabEnum.Detected,
  [DomainNamesRouteEnum.Requested]: MarqCommonEnumsSwWebsiteBrandDomainNamesTabEnum.Requested,
  [DomainNamesRouteEnum.Reported]: MarqCommonEnumsSwWebsiteBrandDomainNamesTabEnum.Reported,
  [DomainNamesRouteEnum.Removed]: MarqCommonEnumsSwWebsiteBrandDomainNamesTabEnum.Removed,
  [DomainNamesRouteEnum.Dismissed]: MarqCommonEnumsSwWebsiteBrandDomainNamesTabEnum.Dismissed,
  [DomainNamesRouteEnum.Denied]: MarqCommonEnumsSwWebsiteBrandDomainNamesTabEnum.Denied,
  [DomainNamesRouteEnum.NonEnforceable]: MarqCommonEnumsSwWebsiteBrandDomainNamesTabEnum.NonEnforceable,
  [DomainNamesRouteEnum.Watchlist]: MarqCommonEnumsSwWebsiteBrandDomainNamesTabEnum.Watchlist,
  [DomainNamesRouteEnum.Monitoring]: MarqCommonEnumsSwWebsiteBrandDomainNamesTabEnum.Monitoring,
};

export const useRequest = () => {
  const { brandId } = useSelectedBrandId();

  const { currentTab } = useTab();
  const tab = requestTab[currentTab];
  const filterParams = useQueryParams<FilterQueryParams>();

  const {
    scanned_date_end,
    scanned_date_start,
    added_date_start,
    added_date_end,
    requested_date_start,
    requested_date_end,
    custom_tag_id_filter,
    domain_status_filter,
    comment_user_type_filter,
    ...restParams
  } = filterParams.params;

  const { country_filter } = filterParams.stringParams;
  const countryFilter = country_filter ? parseValueToValueArray(country_filter) : [];
  const domainStatusFilter = domain_status_filter ? parseValueToValueArray(domain_status_filter) : [];
  const commentUserTypeFilter = comment_user_type_filter
    ? parseValueToValueArray<MarqCommonEnumsCommentCommentUserTypeEnum>(comment_user_type_filter)
    : [];

  const scannedDateFilter: MarqCommonModelLocalDateRange | undefined =
    scanned_date_start && scanned_date_end
      ? {
          start: scanned_date_start,
          end: scanned_date_end,
        }
      : undefined;
  const added_date_filter: MarqCommonModelLocalDateRange | undefined =
    added_date_start && added_date_end
      ? {
          start: added_date_start,
          end: added_date_end,
        }
      : undefined;
  const requested_date_filter: MarqCommonModelLocalDateRange | undefined =
    requested_date_start && requested_date_end
      ? {
          start: requested_date_start,
          end: requested_date_end,
        }
      : undefined;

  const customTagIdFilter =
    !custom_tag_id_filter || Array.isArray(custom_tag_id_filter) ? custom_tag_id_filter : [custom_tag_id_filter];

  const handleSortChange = (value: SortValue) => {
    const { sortedColumn, sortedOrder } = getSort(value);

    filterParams.setParams({ sorted_column: sortedColumn, sorted_order: sortedOrder });
  };

  const handleBrandChange = (brandId?: number) => {
    if (!brandId) {
      filterParams.removeParams('brand_id');
      return;
    }

    filterParams.setParams({ brand_id: brandId });
  };

  const handleOffsetChange = (offset: number) => {
    filterParams.setParams({ offset });
  };

  const handleCommentUserTypeChange = (commentUserType: MarqCommonEnumsCommentCommentUserTypeEnum[]) => {
    filterParams.setParams({ comment_user_type_filter: commentUserType });
  };

  return {
    request: {
      brand_id: brandId,
      ...restParams,
      offset: filterParams.params.offset || 0,
      search: filterParams.params.search ? decodeURIComponent(filterParams.params.search) : undefined,
      country_filter: countryFilter?.map((country) => country.toString() as MarqFoundationEnumsCountryCodeEnum),
      scanned_date_filter: scannedDateFilter,
      custom_tag_id_filter: customTagIdFilter || [],
      tab,
      requested_date_filter: requested_date_filter,
      added_date_filter: added_date_filter,
      domain_status_filter: domainStatusFilter,
      comment_user_type_filter: commentUserTypeFilter,
    },
    sort: {
      sorted_column: filterParams.params.sorted_column || getSort(SORT_LIST[currentTab]?.[0]).sortedColumn,
      sorted_order: filterParams.params.sorted_order || getSort(SORT_LIST[currentTab]?.[0]).sortedOrder,
    },
    onSortChange: handleSortChange,
    onBrandChange: handleBrandChange,
    onOffsetChange: handleOffsetChange,
    onCommentUserTypeChange: handleCommentUserTypeChange,
  };
};

const getSort = (value?: SortValue) => {
  const [sortedColumn, sortedOrder] = (value ?? '').split('+') as [
    MarqBrandEnumsWebsiteBrandSortedColumnEnum,
    MarqCommonEnumsSortedOrderEnum
  ];

  return {
    sortedColumn,
    sortedOrder,
  };
};
